/**
 * The 2D plan's drafting grid, as both platforms draw it: a line every
 * `gridStep` feet, every fifth one major, stepping up by fives as the plan
 * zooms out so lines never stand closer than `GRID_MIN_PX`. A platform draws
 * it as two SVG paths, the minor lines and the major ones (`planGridPaths`):
 * the spacing keeps the number of lines on the stage bounded by its size at
 * any zoom, so each frame's cost and memory stay bounded too.
 */
import { cssColor, PALETTE } from './palette';
import type { Viewport } from './viewport';

/** Grid lines never draw closer together than this (px): zoomed out, the spacing steps up 1, 5, 25 ft and on. */
export const GRID_MIN_PX = 8;

/** A grid line's width on screen (px), in the plan and the 3D view alike. */
export const GRID_LINE_WIDTH = 1;

/** Every this many grid lines, one is major. */
const MAJOR_EVERY = 5;

/** The grid spacing (ft) at a zoom of `scale` px per foot: a foot, stepping up by fives until lines stand at least `GRID_MIN_PX` apart. */
export function gridStep(scale: number): number {
  let step = 1;
  while (step * scale < GRID_MIN_PX) step *= MAJOR_EVERY;
  return step;
}

/**
 * The grid on the stage, as two SVG paths: draw `minorPath` and then
 * `majorPath`, each with no fill, stroked `strokeWidth` in `minorColor` or
 * `majorColor`.
 */
export interface PlanGridPaths {
  minorPath: string;
  majorPath: string;
  strokeWidth: number;
  minorColor: string;
  majorColor: string;
}

const MAJOR_COLOR = cssColor(PALETTE.gridMajor);
const MINOR_COLOR = cssColor(PALETTE.gridMinor);

/** How far past the stage's edge a line may sit and still count as on it (px): rounding, never a visible line. */
const EDGE_SLACK = 1e-6;

/** A line's place on screen, to a ten-thousandth of a pixel, as few characters as that takes. */
const screenCoordinate = (value: number): string => String(Math.round(value * 1e4) / 1e4);

/**
 * The grid on a stage `width` by `height` at `viewport`: every line whose
 * place is on the stage, each once, a vertical `M x 0 V height` or a
 * horizontal `M 0 y H width` segment across the whole stage, in the major
 * path every fifth step and in the minor path otherwise. Lines stand at
 * least `GRID_MIN_PX` apart, so the paths hold at most the stage's width and
 * height over that, plus one each way, at any zoom.
 */
export function planGridPaths({ scale, offset }: Viewport, width: number, height: number): PlanGridPaths {
  const stepPixels = gridStep(scale) * scale;
  const minor: string[] = [], major: string[] = [];
  const addLines = (origin: number, extent: number, segment: (at: string) => string): void => {
    for (let index = Math.ceil((-EDGE_SLACK - origin) / stepPixels); origin + index * stepPixels <= extent + EDGE_SLACK; index++) {
      (index % MAJOR_EVERY === 0 ? major : minor).push(segment(screenCoordinate(origin + index * stepPixels)));
    }
  };
  addLines(offset.x, width, x => `M${x} 0V${height}`);
  addLines(offset.y, height, y => `M0 ${y}H${width}`);
  return { minorPath: minor.join(''), majorPath: major.join(''), strokeWidth: GRID_LINE_WIDTH, minorColor: MINOR_COLOR, majorColor: MAJOR_COLOR };
}
